import {Phone, Calendar, Palette, Check, MapPin, Award, Sparkles} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import CanonicalTag from '@/components/CanonicalTag'

const ChromeDeleteSanDiegoPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    // Add FAQ Schema
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does chrome delete cost in San Diego?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Chrome delete pricing ranges from $400-$1,200 depending on vehicle size and chrome coverage. Tesla Model 3/Y typically cost $600-$800. Luxury vehicles with extensive chrome may cost more."
          }
        },
        {
          "@type": "Question",
          "name": "How long does chrome delete last?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Professional chrome delete lasts 5-7 years with quality vinyl. Properly installed vinyl maintains appearance and adhesion for the full lifespan with minimal maintenance."
          }
        },
        {
          "@type": "Question",
          "name": "Is chrome delete reversible?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Vinyl chrome delete is fully reversible. We can remove the vinyl at any time, revealing the original chrome underneath in perfect condition."
          }
        },
        {
          "@type": "Question",
          "name": "Does chrome delete damage the chrome trim?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. Professional vinyl application does not damage chrome. The vinyl protects chrome from oxidation and corrosion, actually preserving it better than leaving it exposed."
          }
        },
        {
          "@type": "Question",
          "name": "Can chrome delete be combined with other services?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Most clients combine chrome delete with PPF, window tint, or vinyl wraps for a complete vehicle transformation. Package discounts available."
          }
        },
        {
          "@type": "Question",
          "name": "What finishes are available for chrome delete?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Gloss black, matte black, satin black are most popular. We also offer brushed aluminum, gunmetal gray, carbon fiber, and color-matched finishes to match your vehicle's paint."
          }
        }
      ]
    })
    document.head.appendChild(script)

    // Set meta tags
    document.title = "Chrome Delete San Diego | Professional Vinyl Chrome Blackout Services"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert chrome delete installation in San Diego. Black out chrome trim on Tesla, BMW, Audi, Mercedes. Gloss, matte, satin finishes. Serving Vista, Carlsbad, Oceanside. Free quotes.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/chrome-delete-san-diego" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Chrome Delete San Diego - Professional Vinyl Chrome Blackout Services</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Transform your vehicle's appearance with professional chrome delete installation in San Diego. Our expert installers use premium vinyl to black out chrome trim, creating a sleek, modern aesthetic that's reversible and protective—popular on Tesla, BMW, Audi, Mercedes, and luxury vehicles.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving San Diego County including Vista, Carlsbad, Oceanside, Encinitas, and Rancho Santa Fe. We offer gloss black, matte black, satin black, and color-matched finishes with precision installation techniques and long-lasting durability.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free Chrome Delete Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Chrome Delete */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why San Diego Vehicle Owners Choose Chrome Delete</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Chrome delete has become one of the most popular automotive customization trends in San Diego, transforming vehicles with shiny chrome trim into sleek, modern machines with blacked-out trim. This simple modification dramatically changes your vehicle's appearance, creating a more aggressive, sophisticated aesthetic while remaining fully reversible.
            </p>
            <p>
              Modern vehicles—especially Tesla Model 3, Model Y, Audi, BMW, and Mercedes—feature chrome window trim, door handles, grilles, and badges that many owners find dated or flashy. <a href="/chrome-delete" className="text-cyan-400 hover:underline">Chrome delete services</a> use high-quality automotive vinyl to cover these chrome elements in gloss black, matte black, satin black, or color-matched finishes, creating a cohesive, premium appearance.
            </p>
            <p>
              Beyond aesthetics, chrome delete protects your vehicle's original chrome from San Diego's harsh coastal environment. Salt air causes chrome to pit, corrode, and oxidize over time—vinyl wrap creates a protective barrier preventing this damage. Many clients in <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a>, <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a>, and other coastal areas choose chrome delete specifically for corrosion protection.
            </p>
            <p>
              Chrome delete is frequently combined with other services for comprehensive vehicle transformation. Pair it with <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">paint protection film</a> for ultimate protection, <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">ceramic window tint</a> for enhanced privacy and heat rejection, or a full <a href="/car-wrap-san-diego" className="text-cyan-400 hover:underline">color change wrap</a> for a complete makeover. Our <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista shop</a> handles all services under one roof with package discounts available.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-4 text-white text-center">Chrome Delete Pricing in San Diego</h2>
          <p className="text-center text-gray-400 mb-12 max-w-3xl mx-auto">
            The prices below are starting ranges based on typical vehicle sizes and chrome coverage. Final pricing is determined during your free consultation after we inspect your specific vehicle and chrome elements.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Basic Chrome Delete</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $400</div>
              <p className="text-gray-400 text-sm mb-6">Compact vehicles, minimal chrome</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Window trim blacking out</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Door handle chrome delete</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Gloss or matte black vinyl</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>5-7 year lifespan</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Complete Chrome Delete</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $700</div>
              <p className="text-gray-400 text-sm mb-6">Mid-size sedans and SUVs</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All window trim and pillars</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Door handles and mirrors</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Grille and badge chrome</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Premium vinyl materials</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Luxury Vehicle Chrome Delete</h3>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $900</div>
              <p className="text-gray-400 text-sm mb-6">Large SUVs and luxury vehicles</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All exterior chrome elements</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Complex trim disassembly</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Custom color matching available</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Premium finish guarantee</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400">
            Package discounts when combined with <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">PPF</a> or <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">window tint</a>.
          </p>
        </div>
      </section>

      {/* Popular Vehicles */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Popular Vehicles for Chrome Delete in San Diego</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Tesla Model 3 & Model Y</h3>
              <p className="text-gray-300 mb-4">The most requested chrome delete in San Diego. We black out window trim and door handles on thousands of Teslas annually, creating the clean blacked-out aesthetic Tesla owners love.</p>
              <p className="text-cyan-400 font-semibold text-sm mb-2">$600 - $800</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Window trim (all 4 doors)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Door handles</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Optional: badge overlays</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">BMW 3 Series & 5 Series</h3>
              <p className="text-gray-300 mb-4">Transform your BMW with gloss black trim delete. We wrap window trim, kidney grilles, and side vents for an aggressive M-Sport inspired look without chrome shine.</p>
              <p className="text-cyan-400 font-semibold text-sm mb-2">$700 - $1,000</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Kidney grille surround</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Window trim all pillars</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Side vent trim</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Mercedes C-Class & E-Class</h3>
              <p className="text-gray-300 mb-4">Eliminate Mercedes chrome for a more aggressive AMG-style appearance. We vinyl wrap all chrome trim elements creating a cohesive blacked-out luxury look.</p>
              <p className="text-cyan-400 font-semibold text-sm mb-2">$800 - $1,100</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Grille star and trim</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Window trim and pillars</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Door handle trim</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Audi A4, A6, Q5, Q7</h3>
              <p className="text-gray-300 mb-4">Audi's prominent chrome trim responds beautifully to vinyl blackout treatment. Create a cohesive S-Line or RS-inspired appearance with comprehensive chrome delete.</p>
              <p className="text-cyan-400 font-semibold text-sm mb-2">$700 - $1,000</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Front grille surround</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Window and pillar trim</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Mirror caps and badges</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Range Rover & Luxury SUVs</h3>
              <p className="text-gray-300 mb-4">High-end SUVs with extensive chrome benefit dramatically from professional delete services. We create stealthy, commanding presence replacing bright chrome with sophisticated black trim.</p>
              <p className="text-cyan-400 font-semibold text-sm mb-2">$900 - $1,200</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Extensive chrome coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Body side trim and roof rails</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Premium satin black finish</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Custom Chrome Delete</h3>
              <p className="text-gray-300 mb-4">Need specific trim elements blacked out? We offer à la carte chrome delete for individual components, partial coverage, or custom color-matched finishes beyond standard black.</p>
              <p className="text-cyan-400 font-semibold text-sm mb-2">Starting at $150/item</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Individual trim pieces</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Custom color matching</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Budget-friendly options</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Our Chrome Delete Installation Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Free Consultation & Finish Selection</h3>
                <p className="text-gray-300">We inspect your vehicle's chrome trim and discuss finish options: gloss black (high-shine matching body paint), matte black (flat stealthy look), satin black (subtle semi-gloss), brushed aluminum, carbon fiber, or custom color matching. We'll show you samples and examples of each finish on similar vehicles to help you visualize the final result.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Chrome Surface Preparation</h3>
                <p className="text-gray-300">All chrome surfaces are thoroughly cleaned with automotive-grade cleaner and degreaser to remove any wax, polish, oils, or contaminants. Proper surface preparation is critical for long-lasting vinyl adhesion. We use isopropyl alcohol final wipe to ensure perfectly clean chrome ready for vinyl application.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Precision Vinyl Application</h3>
                <p className="text-gray-300">Using premium automotive-grade vinyl, we carefully apply material to each chrome element with professional techniques—proper stretching around curves, post-heating for permanent adhesion, and precise trimming for clean edges. Complex trim pieces may be removed for complete wraparound coverage ensuring no visible chrome edges.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Quality Inspection & Delivery</h3>
                <p className="text-gray-300">Final inspection ensures bubble-free application, perfect edges, and complete chrome coverage with no gaps or lifting. We provide care instructions for maintaining your chrome delete and preserving the vinyl finish. Your transformed vehicle is ready to enjoy immediately with no curing time required.</p>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Chrome Delete FAQs - San Diego</h2>
          <div className="space-y-6">
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does chrome delete cost in San Diego?</h3>
              <p className="text-gray-300">Chrome delete pricing ranges from $400-$1,200 depending on vehicle size and chrome coverage. <a href="/tesla-ppf-san-diego" className="text-cyan-400 hover:underline">Tesla</a> Model 3 and Model Y typically cost $600-$800 for complete window trim and door handle coverage. Luxury vehicles with extensive chrome (Range Rover, Mercedes GLS, BMW X7) may cost $900-$1,200. We provide exact pricing after inspecting your vehicle's chrome elements.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does chrome delete last?</h3>
              <p className="text-gray-300">Professional chrome delete using premium automotive vinyl lasts 5-7 years in San Diego with proper care. Longevity depends on sun exposure, maintenance, and vinyl quality. Garaged vehicles last longest. Our installations use top-tier 3M and Avery Dennison vinyl designed specifically for California's intense UV environment. Properly installed vinyl maintains appearance and adhesion for the full lifespan with minimal maintenance required.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Is chrome delete reversible?</h3>
              <p className="text-gray-300">Yes. Vinyl chrome delete is fully reversible. Unlike painting chrome (which is permanent), vinyl can be removed at any time revealing the original chrome underneath in perfect condition. This makes chrome delete ideal for leased vehicles or owners who want flexibility to change back later. Professional removal takes 1-2 hours and leaves no residue or damage.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does chrome delete damage the chrome trim?</h3>
              <p className="text-gray-300">No. Professional vinyl application does not damage chrome when installed and removed correctly. The vinyl actually protects chrome from oxidation, corrosion, and pitting caused by San Diego's coastal salt air. Chrome underneath vinyl-wrapped trim is often in better condition than exposed chrome after years of environmental exposure.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can chrome delete be combined with other services?</h3>
              <p className="text-gray-300">Yes. Most clients combine chrome delete with complementary services for complete vehicle transformation. Popular combinations: chrome delete + <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">paint protection film</a> (ultimate protection with style), chrome delete + <a href="/window-tint-san-diego" className="text-cyan-400 hover:underline">ceramic window tint</a> (complete blacked-out look), chrome delete + <a href="/car-wrap-san-diego" className="text-cyan-400 hover:underline">color change wrap</a> (total transformation). Package discounts available.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What finishes are available for chrome delete?</h3>
              <p className="text-gray-300">Most popular: Gloss black (high-shine matches paint), Matte black (flat non-reflective stealth look), Satin black (subtle semi-gloss). Also available: Brushed aluminum (metallic texture), Gunmetal gray (dark metallic), Carbon fiber (3D texture), Custom color-matched to your vehicle's paint. We'll show you samples during consultation to help choose the perfect finish for your aesthetic goals.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does chrome delete installation take?</h3>
              <p className="text-gray-300">Most chrome delete installations take 2-4 hours for basic coverage (window trim and door handles). Comprehensive chrome delete on luxury vehicles with extensive chrome elements may take a full day. The vinyl is ready to use immediately after installation with no curing time required. Same-day service often available for standard Tesla Model 3 and Model Y chrome delete.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Will chrome delete affect resale value?</h3>
              <p className="text-gray-300">Chrome delete typically increases resale appeal, especially for vehicles where blacked-out trim is popular (Tesla, BMW, Audi). The vinyl protects original chrome preserving it in perfect condition underneath. Many buyers specifically seek vehicles with chrome delete already completed. If a future buyer prefers chrome, the vinyl removes cleanly revealing factory chrome.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
          <p className="text-gray-300 text-center mb-8">
            Complete your vehicle's blacked-out transformation with these complementary services:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/chrome-delete" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Chrome Delete Services</h3>
              <p className="text-gray-400 text-sm">Complete chrome delete guide covering all finishes, application techniques, and vehicle types</p>
            </a>
            <a href="/headlight-taillight-tint" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Headlight & Taillight Tinting</h3>
              <p className="text-gray-400 text-sm">Professional light tinting for a cohesive blacked-out aesthetic with legal smoke and smoked finishes</p>
            </a>
            <a href="/window-tinting" className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Window Tinting Services</h3>
              <p className="text-gray-400 text-sm">Ceramic window tint for privacy, heat rejection, and UV protection to complete your blacked-out look</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Chrome Delete Services Across San Diego County
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Located in <a href="/ppf-vista" className="text-cyan-400 hover:underline font-semibold">Vista</a>, we provide professional chrome delete services to vehicle owners throughout San Diego County.
          </p>
          <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-sm">
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County Coastal</h3>
              <ul className="space-y-1">
                <li>• <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a></li>
                <li>• <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a></li>
                <li>• <a href="/ppf-encinitas" className="text-cyan-400 hover:underline">Encinitas</a></li>
                <li>• Solana Beach</li>
                <li>• Del Mar</li>
              </ul>
            </div>
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County Inland</h3>
              <ul className="space-y-1">
                <li>• <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a> (Shop Location)</li>
                <li>• <a href="/ppf-rancho-santa-fe" className="text-cyan-400 hover:underline">Rancho Santa Fe</a></li>
                <li>• San Marcos</li>
                <li>• Escondido</li>
                <li>• Poway</li>
              </ul>
            </div>
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">Greater San Diego</h3>
              <ul className="space-y-1">
                <li>• <a href="/ppf-san-diego" className="text-cyan-400 hover:underline">San Diego</a></li>
                <li>• La Jolla</li>
                <li>• Mission Valley</li>
                <li>• Downtown</li>
                <li>• All SD County</li>
              </ul>
            </div>
          </div>
          <p className="text-gray-300 text-center mt-8">
            <strong className="text-white">Shop Address:</strong> 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Transform Your Vehicle with Professional Chrome Delete</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free chrome delete consultation today. View finish samples and get exact pricing for your vehicle. Same-day installation often available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free Chrome Delete Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default ChromeDeleteSanDiegoPage
